import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import { RESET_PASSWORD_LABELS } from '../../../utils/labels';

const MIN_PASSWORD_LENGTH = 8;

interface UseNewPasswordReturn {
  password: string;
  setPassword: (value: string) => void;
  confirm: string;
  setConfirm: (value: string) => void;
  error: string;
  loading: boolean;
  ready: boolean;
  submit: (e: FormEvent) => Promise<void>;
}

export function useNewPassword(): UseNewPasswordReturn {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        setReady(true);
      } else {
        navigate('/login', { replace: true });
      }
    });
  }, [navigate]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(RESET_PASSWORD_LABELS.passwordTooShort);
      return;
    }
    if (password !== confirm) {
      setError(RESET_PASSWORD_LABELS.passwordMismatch);
      return;
    }

    setLoading(true);
    try {
      const { error: updateError } = await supabase.auth.updateUser({ password });
      if (updateError) {
        setError(updateError.message);
        return;
      }
      window.location.href = '/';
    } catch {
      setError(RESET_PASSWORD_LABELS.genericError);
    } finally {
      setLoading(false);
    }
  };

  return { password, setPassword, confirm, setConfirm, error, loading, ready, submit };
}
